import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  forwardRef,
  RefObject,
  ForwardedRef,
  MouseEvent,
  useMemo,
  memo,
} from 'react';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import useMediaQuery from '@mui/material/useMediaQuery';
import dayjs, {
  Dayjs,
} from 'utils/dayjs';
import {
  useMedia,
} from 'core/hooks';
import {
  pluralize,
} from '@populateui/utils';
import VirtualList from 'core/components/VirtualList';
import {
  FilterContext,
} from 'core/Filters/context';
import Spinner from 'pages/Dashboard/components/Spinner';
import DatePicker from 'core/components/DateTimePicker/SingleDatePicker';
import PatientDetailsCard from 'pages/Dashboard/pages/Encounters/pages/List/components/PatientDetails';
import PatientRow from 'pages/Dashboard/pages/Encounters/pages/List/components/PatientRow';
import SettingsButton from 'pages/Dashboard/pages/Appointments/pages/List/components/SettingsButton';
import {
  EncounterViewModel,
  PatientDTO,
  PrintEncounterScheduleRequestDTO,
} from 'dtos';
import Button from 'pages/Dashboard/components/Button';
import PrinterIcon from 'core/icons/printer';
import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import {
  postApiEncounterPrintEncounterSchedule,
} from 'endpoints';
import {
  usePrintMediaRequest,
} from 'views/EMR/hooks/media';
import {
  formatDateOnly,
} from 'utils/date';
import PatientHeader from 'pages/Dashboard/components/PatientHeader';
import {
  ViewContext,
} from 'pages/Dashboard/contexts/viewContext';
import PanelExtension from 'core/components/PanelExtension';
import map from 'lodash/map';
import compact from 'lodash/compact';
import {
  useEncounterNotificationEffect,
} from 'pages/Dashboard/pages/Encounters/pages/List/hooks';
import {
  useEncounterSettings,
} from 'pages/Dashboard/pages/Encounters/hooks';
import {
  EncounterDoctor,
} from 'pages/Dashboard/pages/Encounters/types/settings';
import keyBy from 'lodash/keyBy';
import PCCActions from 'pages/Dashboard/pages/Encounters/pages/List/components/PCCActions';
import useEMRAccess from 'views/EMR/hooks/useEMRAccess';
import {
  MediaOriginatorType,
} from 'pages/Dashboard/utils/constants';

type Props = {
  date: Dayjs;
  setDate: (value: Dayjs | null) => void;
};

type SelectedEncounterResult = {
  selectedEncounter: EncounterViewModel | undefined;
  selectEncounter: (encounter: EncounterViewModel) => void;
  clearEncounter: () => void;
};

/*
*Below 1280px - button to icon only
*/

export const UPCOMING_ENCOUNTERS_REGEX = /^(no show)|(cancelled)|(checked out)|(note finalized)$/i;

export function useSelectedEncounter(
  encounters: EncounterViewModel[],
): SelectedEncounterResult {
  const isSplit = useMediaQuery('(min-width: 1025px)', { noSsr: true });
  const wasSplitRef = useRef(isSplit);
  const [selectedId, setSelectedId] = useState<number>();

  const selectedEncounter = isNil(selectedId)
    ? undefined
    : encounters.find((encounter) => encounter.encounterId === selectedId);

  useEffect(() => {
    const leftSplit = wasSplitRef.current && !isSplit;
    wasSplitRef.current = isSplit;

    const exists = !isNil(selectedId) && encounters.some(
      (encounter) => encounter.encounterId === selectedId,
    );

    if (leftSplit || !exists) {
      setSelectedId(isSplit ? encounters[0]?.encounterId : undefined);
    }
  }, [encounters, selectedId, isSplit]);

  return {
    selectedEncounter,
    selectEncounter(encounter: EncounterViewModel) {
      setSelectedId(encounter.encounterId);
    },
    clearEncounter() {
      setSelectedId(undefined);
    },
  };
}

const _PatientList = forwardRef(({
  date,
  setDate,
}: Props, ref: ForwardedRef<HTMLElement>) => {
  const {
    isLoading,
    filteredData: encounters,
    refetch,
  } = useContext(FilterContext);
  const {
    isRestricted: hasLimitedAccess,
    isPCCActionAvailable,
  } = useEMRAccess();

  const { extensionExpanded, setExtensionExpanded, Extension } = useContext(ViewContext);

  const { requestMedia } = useMedia();
  const { print, isPrinting } = usePrintMediaRequest<[PrintEncounterScheduleRequestDTO]>(
    postApiEncounterPrintEncounterSchedule,
  );

  const {
    selectedEncounter,
    selectEncounter,
    clearEncounter,
  } = useSelectedEncounter(encounters);
  const now = new Date();
  const isSameDayOrAfter = date?.isSame(now, 'day') || date?.isAfter(now);

  const { doctors } = useEncounterSettings();

  const providerByUserId = useMemo(() => (
    keyBy(doctors, 'userId') as Record<number, EncounterDoctor>
  ), [doctors]);

  const upcomingEncounters = useMemo(() => (
    encounters?.filter((encounter: EncounterViewModel) => (
      !UPCOMING_ENCOUNTERS_REGEX.test(encounter?.patientAppointmentStatus ?? '')
    ))
  ), [encounters]);

  useEffect(() => {
    const patientIds = compact(map(encounters, 'patient.patientId'));
    const userIds = compact(map(encounters, 'userId'));
    if (patientIds.length > 0) {
      requestMedia(patientIds);
    }
    if (userIds.length > 0) {
      requestMedia(userIds, MediaOriginatorType.User);
    }
  }, [encounters, selectedEncounter, requestMedia]);

  useEffect(() => {
    if (isNil(date)) {
      setDate(dayjs());
    }
  }, [date]);

  useEncounterNotificationEffect(date);

  const encounterIds = encounters?.map(({ encounterId }) => encounterId);

  const handlePrintSchedule = async () => {
    await print([{
      encounterDate: formatDateOnly(date),
      encounterIds,
    }]);
  };

  return (
    <div className="bg-primaryLight relative flex overflow-y-hidden h-full">
      <section className={clsx(
        'grow w-0 min-w-0 h-full flex flex-col bg-white px-4 pt-4 rounded-xl',
        { 'lg:rounded-l-xl lg:rounded-r-none': !isNil(selectedEncounter) },
      )}
      >
        <div className="flex justify-between items-center mb-4">
          <h1 className="font-semibold text-xl">Encounters</h1>
          {isSameDayOrAfter && !isLoading && upcomingEncounters?.length > 0 && (
            <p className="ml-3 text-sm font-normal text-gray-400">
              {upcomingEncounters?.length ?? 0}
              {' '}
              of
              {' '}
              {pluralize('encounter', encounters?.length ?? 0)}
              {' '}
              left
            </p>
          )}
        </div>
        <div className="flex mb-4 justify-between items-center relative lg:z-20">
          <div className="w-full flex items-center gap-1 xl:gap-3">

            <Button
              variant="outlined"
              type="button"
              onClick={() => { setDate(dayjs()); }}
            >
              Today
            </Button>
            <DatePicker
              className="w-32 date-picker-input-semibold"
              showDateButtons
              value={date}
              onChange={setDate}
            />
            <SettingsButton
              filtersRef={ref as RefObject<HTMLDivElement>}
              label="Filters"
              pageObject="encounter"
              onlyFilters
            />
          </div>
          {!hasLimitedAccess && encounterIds.length !== 0 && (
            <Button
              variant="outlined"
              type="button"
              className="!h-8 flex gap-2 xl:!pl-2.5 xl:!min-w-[144px]"
              disabled={isPrinting}
              onClick={handlePrintSchedule}
            >
              {isPrinting ? <Spinner /> : <PrinterIcon className="!w-4 !h-4 !text-inherit" />}
              <span className='lg:hidden xl:block whitespace-nowrap'>
                {isPrinting ? 'Printing...' : 'Print Schedule'}
              </span>
            </Button>
          )}
        </div>
        {isLoading && (
          <div className="flex flex-1 justify-center items-center">
            <Spinner className="!font-bold" size={30} />
          </div>
        )}
        {!isLoading && encounters.length > 0 && (
          <VirtualList<EncounterViewModel>
            renderItemComponent={(encounter: EncounterViewModel) => {
              const { encounterId } = encounter;
              return (
                <PatientRow
                  encounter={encounter}
                  provider={providerByUserId[encounter.userId ?? 0]}
                  selected={
                  selectedEncounter?.encounterId === encounterId
                }
                  onClick={(e: MouseEvent<HTMLElement>) => {
                    if (e.metaKey || e.ctrlKey) {
                      window.open(`/encounters/${encounterId}`);
                    }
                    selectEncounter(encounter);
                  }}
                  refetch={refetch}
                />
              );
            }}
            itemSize={64}
            data={encounters}
            className="-mx-4"
            scrollContainerClassName="px-4 pb-4"
          />
        )}
        {!isLoading && !encounters.length && (
          <div className="flex flex-1 justify-center items-center">
            <span className="font-semibold">No Encounters Found</span>
          </div>
        )}
      </section>
      <aside className="flex flex-col overflow-x-hidden empty:hidden absolute inset-0 z-10 w-full lg:static lg:z-auto lg:flex-initial lg:!w-1/2 xl:!w-5/12">

        {!isNil(selectedEncounter) && (
          <div className={clsx(
            'flex flex-col bg-gray-100 w-full h-full rounded-xl lg:rounded-r-xl lg:rounded-l-none',
            { 'gap-2': isPCCActionAvailable },
          )}
          >
            <button
              type="button"
              className="flex items-center gap-2 px-4 pt-3 pb-1 text-sm font-semibold text-blue-500 w-fit lg:hidden"
              onClick={() => {
                setExtensionExpanded?.(null);
                clearEncounter();
              }}
            >
              <KeyboardBackspaceRoundedIcon className="!w-5 !h-5 !text-inherit" />
              Back to Encounters
            </button>
            <PatientHeader
              patient={selectedEncounter?.patient as PatientDTO}
            />
            <PCCActions encounter={selectedEncounter} />
            {!hasLimitedAccess && (
              <div className="flex flex-1 flex-col w-full relative overflow-y-hidden">
                <PatientDetailsCard
                  hasLink
                  encounter={selectedEncounter}
                  tabs={['visitDetails', 'notes', 'imaging', 'report']}
                />
                <div className={clsx('absolute inset-0 w-0 transition-all duration-100', {
                  '!w-full': extensionExpanded,
                })}
                >
                  <PanelExtension className="w-full rounded-r-xl">
                    {!isNil(Extension) && (
                      <Extension
                        patient={selectedEncounter.patient!}
                        className="bg-gray-100"
                        classes={{ list: 'px-4 ' }}
                        onClose={() => { setExtensionExpanded?.(null); }}
                      />
                    )}
                  </PanelExtension>
                </div>
              </div>
            )}
          </div>
        )}
      </aside>
    </div>
  );
});

export default memo(_PatientList);
